/**
 * @typedef {Object} SharedBrowserState
 * @property {number} focusedIndex - Index of the currently focused image.
 * @property {'single'|'group'} viewMode - Current view mode, e.g., 'single' or 'group'.
 * @property {Array} unifiedApidList - List of parent group APIDs in case of split groups.
 * @property {Array} thumbnails - Array of thumbnails, either parent group or child group APIDs.
 * @property {boolean} imageLoaded - Loading state of the image.
 * @property {string|null} imageError - Error message if the image fails to load.
 */

export const initialState = {
  focusedIndex: -1,
  viewMode: 'single',

  /**
   * In the case of a split group (the group of images has been subdivided into 'items'),
   *   this is always the parent group of apids, even when viewing an item (child group)
   *   It is used to determine the new value of the focusedIndex when switching between view modes
   *   (which changes the array of images that focusedIndex refers to)
   *
   * In the case of an unsplit group, this has no value
   */
  unifiedApidList: [],

  thumbnails: [], // This is either the parent group of apids during viewMode = 'group', or a single child group of apids during viewMode = 'single'
  imageLoaded: false, // Loading state of ComboViewer
  imageError: null,
}

export default function reducer(state, action) {
  if (!state) throw new Error('state is required')
  if (!action) throw new Error('action is required')
  switch (action.type) {
    case 'BATCH':
      return action.value.reduce((acc, curr) => reducer(acc, curr), state)
    case 'SET_FOCUSED_INDEX':
      return { ...state, focusedIndex: action.value }
    case 'SET_VIEW_MODE':
      return { ...state, viewMode: action.value }
    case 'SET_UNIFIED_APID_LIST':
      return { ...state, unifiedApidList: action.value }
    case 'SET_THUMBNAILS':
      return { ...state, thumbnails: action.value }
    case 'SET_IMAGE_LOADED':
      return { ...state, imageLoaded: action.value }
    case 'SET_IMAGE_ERROR':
      return { ...state, imageError: action.value }
    default:
      throw new Error(`Unhandled action type to ImageBrowsersReducer: ${action.type} in action: `, action)
  }
}
